import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { Button, IconButton, InputAdornment, Link, Stack } from '@mui/material'
import { useStytchB2BClient } from '@stytch/react/b2b'
import { logEventsForFirebase } from '@/hooks/auth/useAuthentication'
import { getRedirectUrl, SESSION_DURATION_MINUTES } from '@/utils/auth'
import { showErrorSnackbar } from '@/utils/snackbars'
import { FormTextField, PixyDocsForm } from '@/components/forms'
import { Email, LoginViewOptions } from './Login'
import RecoverPasswordRequested from './request-views/RecoverPasswordRequested'

type FormValues = {
  email: string
  password: string
}

export default function LoginWithPassword({
  stytchOrg,
  resetView,
  setShowOnly,
  ...rest
}: LoginViewOptions) {
  const stytch = useStytchB2BClient()
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [searchParams] = useSearchParams()
  const [view, setView] = useState<
    'show_option' | 'login' | 'recover_password' | 'recover_password_request'
  >('show_option')

  const methods = useForm<FormValues>({
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const {
    formState: { isValid, dirtyFields },
  } = methods

  // For some reason isDirty won't work, but dirtyFields does
  const isDirty =
    view === 'recover_password'
      ? dirtyFields.email
      : dirtyFields.password && dirtyFields.email

  const requestResetEmail = useCallback(
    (email: string) => {
      return stytch.passwords.discovery.resetByEmailStart({
        email_address: email,
        discovery_redirect_url: getRedirectUrl(),
        reset_password_redirect_url: getRedirectUrl(),
      })
    },
    [stytch.passwords],
  )

  const handleOnSubmit = useCallback(
    async (values: FormValues) => {
      try {
        if (view === 'recover_password') {
          requestResetEmail(values.email)
          setView('recover_password_request')
        } else {
          if (stytchOrg) {
            const { member_authenticated, member } =
              await stytch.passwords.authenticate({
                email_address: values.email,
                organization_id: stytchOrg.organization_id,
                password: values.password,
                session_duration_minutes: SESSION_DURATION_MINUTES,
              })

            if (member_authenticated) {
              logEventsForFirebase(
                member.name || member.member_id,
                member.email_address,
              )
            } else {
              throw new Error('Member authentication failed')
            }
          } else {
            const response = await stytch.passwords.discovery.authenticate({
              email_address: values.email,
              password: values.password,
            })
            let params = `token=${response.intermediate_session_token}`
            const returnTo = searchParams.get('return_to')
            if (returnTo) {
              params += `&return_to=${returnTo}`
            }
            navigate(`/authenticate?${params}`, {
              replace: true,
            })
          }
        }
      } catch (e) {
        console.error('Error during sign in:', e)
        showErrorSnackbar(
          'An error occurred during the request. Please try again.',
        )
      }
    },
    [
      view,
      requestResetEmail,
      stytchOrg,
      stytch.passwords,
      searchParams,
      navigate,
    ],
  )

  useEffect(() => {
    if (resetView) {
      setView('show_option')
    }
  }, [resetView])

  if (view === 'recover_password_request') {
    return (
      <RecoverPasswordRequested
        email={rest.email}
        requestResetEmail={requestResetEmail}
      />
    )
  }

  return (
    <Stack spacing={6}>
      {(view == 'login' || view === 'recover_password') && (
        <PixyDocsForm
          methods={methods}
          onSubmit={handleOnSubmit}
          style={{ width: '100%' }}
        >
          <Stack spacing={2}>
            <Email {...rest} />
            {view == 'login' && (
              <FormTextField
                autoComplete="off"
                name="password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                variant="outlined"
                label="Password"
                size="medium"
                required
                useControllerProps={{
                  rules: {
                    validate: (value) => value.trim().length > 0,
                  },
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseUp={(e) => e.preventDefault()}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <VisibilityOff fontSize="inherit" />
                          ) : (
                            <Visibility fontSize="inherit" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
            <Button type="submit" disabled={!isValid || !isDirty}>
              Continue
            </Button>
          </Stack>
        </PixyDocsForm>
      )}
      {view !== 'recover_password' && (
        <Link
          component="button"
          onClick={() => {
            setView(view === 'show_option' ? 'login' : 'recover_password')
            setShowOnly('password')
          }}
          underline="hover"
        >
          {view === 'show_option'
            ? 'Use a password instead'
            : 'Forgot password?'}
        </Link>
      )}
    </Stack>
  )
}
